/**
 * 8 domains for ethic survey questions 1–52.
 * Domain ranges are inclusive (1-based).
 * Labels use original Thai text (คณะทำงาน Ethic).
 */
export interface EthicDomain {
  id: string;
  label: string;
  questions: [number, number]; // [start, end] inclusive
}

export const ETHIC_DOMAINS: EthicDomain[] = [
  {
    id: 'religious',
    label: 'การเปิดโอกาสให้ผู้ป่วย/ญาติปฏิบัติศาสนกิจ',
    questions: [1, 2],
  },
  {
    id: 'unit_support',
    label: 'การสนับสนุนและรับฟังของหน่วยงาน/หัวหน้าหน่วยงาน',
    questions: [3, 6],
  },
  {
    id: 'professional_respect',
    label: 'การให้เกียรติ อิสระ และการพัฒนาศักยภาพ',
    questions: [7, 12],
  },
  {
    id: 'team_support',
    label: 'การสนับสนุนและการทำงานเป็นทีม',
    questions: [13, 20],
  },
  {
    id: 'non_harassment',
    label: 'ไม่ทำร้าย/ดูหมิ่น/เลือกปฏิบัติ',
    questions: [21, 29],
  },
  {
    id: 'fairness',
    label: 'ความเท่าเทียมและความเป็นธรรม',
    questions: [30, 35],
  },
  {
    id: 'truthfulness',
    label: 'ความจริงและความรับผิดชอบ',
    questions: [36, 42],
  },
  {
    id: 'confidentiality',
    label: 'การรักษาความลับและการรักษาสัญญา',
    questions: [43, 52],
  },
];

/**
 * Original Thai text for each survey question (1–52).
 * คณะทำงาน Ethic — ส่งเสริมจริยธรรมวิชาชีพและพฤติกรรมบริการ
 */
export const ETHIC_QUESTION_TEXTS: Record<number, string> = {
  1: 'หน่วยงานปรับเปลี่ยนเวลาการจัดกิจกรรมการพยาบาลเพื่อเปิดโอกาสให้ผู้ป่วยได้ปฏิบัติศาสนกิจขณะอยู่ในโรงพยาบาลโดยไม่เกิดผลเสียต่อผู้ป่วย',
  2: 'หน่วยงานจัดสถานที่ให้ญาติ/ผู้ป่วยได้ปฏิบัติศาสนกิจ',
  3: 'หน่วยงานเปิดช่องทางให้ผู้ป่วยและญาติเสนอความคิดเห็นต่อการปฏิบัติงานของบุคลากร',
  4: 'หัวหน้าหน่วยงานจัดตารางการปฏิบัติงานที่เอื้อเวลาให้บุคลากรได้ร่วมกิจกรรมในวันสำคัญทางศาสนา',
  5: 'หัวหน้าหน่วยงานรับฟังปัญหาของพยาบาลด้วยความเต็มใจที่จะให้การช่วยเหลือ',
  6: 'หัวหน้าหน่วยงานรับฟังด้วยความเต็มใจเมื่อพยาบาลปรึกษาหรือรายงานปัญหาของผู้ป่วย',
  7: 'บุคลากรในหน่วยงานให้เกียรติ ยอมรับ และเคารพความคิดเห็นซึ่งกันและกัน',
  8: 'บุคลากรในหน่วยงานสามารถแสดงบทบาทอิสระของวิชาชีพในการดูแลผู้ป่วย',
  9: 'บุคลากรในหน่วยงานมีอิสระในการแสดงความคิดเห็นในกิจกรรมต่างๆ ของหอผู้ป่วย',
  10: 'บุคลากรในหน่วยงานให้อิสระกับผู้ป่วยในการตัดสินใจ โดยการให้ข้อมูลที่ถูกต้องและเพียงพอ',
  11: 'แพทย์รับฟังด้วยความเต็มใจที่จะช่วยเหลือเมื่อพยาบาลรายงานปัญหาของผู้ป่วย',
  12: 'หน่วยงานส่งเสริมการพัฒนาศักยภาพบุคลากร โดยสนับสนุนให้เข้าร่วมประชุม/อบรม/ลาศึกษาต่อ',
  13: 'บุคลากรในหน่วยงานมีการชักถาม ปลอบโยน พูดให้กำลังใจกันเมื่อมีปัญหาเรื่องงาน',
  14: 'บุคลากรในหน่วยงานมีการชักถาม ปลอบโยน พูดให้กำลังใจกันเมื่อมีปัญหาเรื่องส่วนตัว',
  15: 'บุคลากรในหน่วยงานชื่นชมยินดีกับความสำเร็จของเพื่อนร่วมงาน',
  16: 'บุคลากรรู้สึกอบอุ่น/ปลอดภัยขณะปฏิบัติงานร่วมกันในหน่วยงาน',
  17: 'บุคลากรในหน่วยงานมีการช่วยเหลือซึ่งกันและกัน',
  18: 'บุคลากรในหน่วยงานร่วมกันดูแลแก้ไขปัญหาผู้ป่วยโดยไม่รีรอ',
  19: 'บุคลากรในหน่วยงานให้ความร่วมมือในการดูแลผู้ป่วยตามมาตรฐานวิชาชีพ',
  20: 'บุคลากรในหน่วยงานมีความสนใจใฝ่รู้/ปรับปรุงการทำงาน เพื่อให้ผู้ป่วยได้รับการดูแลที่มีประสิทธิภาพ',
  21: 'บุคลากรในหน่วยงานไม่พูดซ้ำเติมเมื่อเพื่อนร่วมงานทำงานผิดพลาด',
  22: 'บุคลากรในหน่วยงานไม่แสดงกิริยาวาจาดูถูก/เหยียดหยามเพื่อนร่วมงานที่มีความคิดเห็นไม่ตรงกับผู้อื่น',
  23: 'บุคลากรในหน่วยงานไม่ใช้กิริยาวาจาไม่สุภาพกับผู้ป่วย',
  24: 'บุคลากรในหน่วยงานไม่แสดงอาการรังเกียจผู้ป่วยที่เป็นโรคติดต่อ',
  25: 'บุคลากรในหน่วยงานไม่ทำให้ผู้ป่วยเจ็บปวดจากการดูแลรักษาโดยไม่มีเหตุผลสมควร',
  26: 'บุคลากรในหน่วยงานไม่ทำให้ผู้ป่วยอับอาย/เสื่อมเสียชื่อเสียง',
  27: 'บุคลากรในหน่วยงานไม่พูดลับหลังให้เพื่อนร่วมงานอับอาย/เสื่อมเสียชื่อเสียง',
  28: 'บุคลากรในหน่วยงานไม่ใช้กิริยาวาจาไม่สุภาพกับเพื่อนร่วมงาน',
  29: 'บุคลากรในหน่วยงานไม่แสดงอาการรังเกียจเพื่อนร่วมงานที่เป็นโรคติดต่อ',
  30: 'หน่วยงานจัดสวัสดิการให้บุคลากรอย่างเท่าเทียมกัน',
  31: 'หน่วยงานมีการลงโทษ/ให้รางวัลแก่บุคลากรด้วยวิธีการที่เป็นธรรม',
  32: 'หัวหน้าหน่วยงานมอบหมายงานให้บุคลากรในหน่วยงานอย่างเท่าเทียมกันตามความรู้ ความสามารถ',
  33: 'บุคลากรในหน่วยงานมีโอกาสในการได้รับการพัฒนาศักยภาพโดยการส่งเข้าอบรมและประชุมอย่างเท่าเทียมกัน',
  34: 'บุคลากรเชื่อมั่นไว้วางใจในความโปร่งใส่ของการพิจารณาความดีความชอบในหน่วยงาน',
  35: 'บุคลากรในหน่วยงานให้การดูแลผู้ป่วยโดยไม่เลือกปฏิบัติ',
  36: 'หัวหน้าหน่วยงานให้ข้อมูลที่เป็นจริงเกี่ยวกับการปฏิบัติงานแก่บุคลากร',
  37: 'หัวหน้าหน่วยงานแจ้งผลการประเมินการปฏิบัติงานที่เป็นจริงต่อบุคลากร',
  38: 'บุคลากรในหน่วยงานให้ข้อมูลที่เป็นจริงในเรื่องเดียวกันแก่ทุกฝ่ายที่เกี่ยวข้อง',
  39: 'บุคลากรในหน่วยงานพูดความจริงต่อกัน',
  40: 'บุคลากรในหน่วยงานเต็มใจที่จะรายงานความผิดพลาดของตนเองต่อหัวหน้า',
  41: 'บุคลากรในหน่วยงานรับผิดชอบต่อความผิดพลาดที่เกิดจากการปฏิบัติงานของตนเอง',
  42: 'บุคลากรในหน่วยงานบันทึกข้อมูลของผู้ป่วยตามความเป็นจริง',
  43: 'หน่วยงานเก็บเอกสารที่เป็นความลับของบุคลากรในที่มิดชิด และผู้ที่ไม่เกี่ยวข้องไม่สามารถเข้าถึงข้อมูลได้',
  44: 'หน่วยงานเก็บเอกสารที่เป็นความลับของผู้ป่วยในที่มิดชิด และผู้ที่ไม่เกี่ยวข้องไม่สามารถเข้าถึงข้อมูลได้',
  45: 'หัวหน้าหน่วยงานไม่นำข้อมูลที่เป็นความลับของผู้ใต้บังคับบัญชาไปเปิดเผย',
  46: 'หัวหน้าหน่วยงานรักษาสัญญาที่ให้ไว้กับผู้ใต้บังคับบัญชา',
  47: 'หัวหน้าหน่วยงานพูดคุย/ให้คำปรึกษาปัญหาของผู้ใต้บังคับบัญชาในห้องทำงานของท่าน/ในสถานที่ที่เหมาะสม',
  48: 'บุคลากรในหน่วยงานไม่นำข้อมูลส่วนตัวที่เพื่อนร่วมงานต้องการปกปิดไปบอกผู้อื่น',
  49: 'บุคลากรในหน่วยงานไม่นำข้อมูลที่เป็นความลับของผู้ป่วยไปเปิดเผยต่อบุคคลที่ไม่เกี่ยวข้อง',
  50: 'บุคลากรในหน่วยงานให้ความระมัดระวังอย่างเคร่งครัดในการบันทึกข้อมูลที่เป็นความลับของผู้ป่วย',
  51: 'บุคลากรในหน่วยงานพูดคุย/ให้คำปรึกษาผู้ป่วยเกี่ยวกับข้อมูลที่เป็นความลับในสถานที่ที่เป็นส่วนตัว',
  52: 'บุคลากรในหน่วยงานทำตามสัญญาที่ให้ไว้กับผู้ป่วย',
};

export function getDomainForQuestion(q: number): EthicDomain | undefined {
  return ETHIC_DOMAINS.find((d) => q >= d.questions[0] && q <= d.questions[1]);
}

export function getQuestionText(q: number): string {
  return ETHIC_QUESTION_TEXTS[q] ?? `ข้อ ${q}`;
}

export function getDomainScores(
  answers: Record<number, number> | number[],
): { domainId: string; mean: number; count: number }[] {
  return ETHIC_DOMAINS.map((domain) => {
    const values: number[] = [];
    for (let q = domain.questions[0]; q <= domain.questions[1]; q++) {
      const v = Array.isArray(answers) ? answers[q - 1] : answers[q];
      if (typeof v === 'number' && !Number.isNaN(v) && v >= 1 && v <= 5) values.push(v);
    }
    const mean = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    return { domainId: domain.id, mean, count: values.length };
  });
}
